import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import awfismojoVideo from '../../../images/awfismojo.mp4';
import styles from './Awfismojo.module.css';

const AwfismojoProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Idea Generation and Conceptualization', icon: '💡' },
    { text: 'User Experience (UX) and Interface (UI) Design', icon: '🎨' },
    { text: 'Technology Integration', icon: '⚡' },
    { text: 'Tailwind CSS Mastery', icon: '🎭' },
    { text: 'Mobile App Development', icon: '📱' },
    { text: 'Content-Centric Approach', icon: '📝' },
    { text: 'Search-Driven Design', icon: '🔍' },
    { text: 'Performance Optimization', icon: '⚙️' },
    { text: 'Aesthetic Innovation', icon: '✨' },
    { text: 'User-Centric Design Thinking', icon: '👥' }
  ];

  const tools = [
    { name: 'WordPress', icon: '🌐', description: 'CMS Platform' },
    { name: 'Figma', icon: '🎨', description: 'Design Tool' },
    { name: 'Photoshop', icon: '🖼️', description: 'Image Editor' },
    { name: 'Illustrator', icon: '✨', description: 'Vector Graphics' }
  ];

  const skills = [
    'CSS', 'Figma', 'HTML5', 'WordPress', 'WooCommerce', 
    'JavaScript', 'Photoshop', 'Illustrator', 'Mobile Design', 'Responsive Design'
  ];

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
      {/* Animated Background */}
      <div className={styles.backgroundGradient}>
        <div className={styles.gradientOrb1}></div>
        <div className={styles.gradientOrb2}></div>
        <div className={styles.gradientOrb3}></div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.heroLeft}>
              <div className={styles.projectBadge}>
                <span className={styles.badgeDot}></span>
                <span>Ongoing Project</span>
              </div>
              
              <h1 className={styles.projectTitle}>Restaurant Ordering Platform</h1>
              <p className={styles.projectSubtitle}>Culinary Diversity from Every Corner</p>
              
              <div className={styles.projectMeta}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Work</span>
                  <span className={styles.metaValue}>Website Design</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Year</span>
                  <span className={styles.metaValue}>2023</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Duration</span>
                  <span className={styles.metaValue}>July 2021 - Ongoing</span>
                </div>
              </div>
              
              <p className={styles.heroDescription}>
                This is a food ordering site created primarily for mobile but also compatible with desktop. Built on WooCommerce, it brings diverse culinary experiences from regional cuisines to your doorstep.
              </p>
            </div>
            
            <div className={styles.heroRight}>
              <div className={styles.heroVisual}>
                <video 
                  className={styles.heroVideo}
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src={awfismojoVideo} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Introduction</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.contentCard}>
            <p className={styles.contentText}>
              Explore a diverse menu inspired by flavors from around the world. From spicy delights of the royal cuisines of Rajasthan, and soon, the freshness of Uttarakhand, It brings the best to your doorstep. Join our community dedicated to savoring the richness of life through exceptional food. We prioritize quality, purity, and an unparalleled customer experience, making every meal a celebration. Choose it for a tasteful, convenient, and delightful culinary journey!
            </p>
          </div>
        </section>

        {/* My Role Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>My Role</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.contentCard}>
            <p className={styles.contentText}>
              As a Mobile Web Developer , I played a pivotal role in shaping the digital experience of users, ensuring seamless navigation and optimal performance on mobile devices. My primary focus was on developing and maintaining the mobile application built on WordPress, providing users with a convenient platform to purchase high-quality, sustainable food products. I ensured the platform delivered exceptional user experiences across all devices while maintaining the highest standards of performance and accessibility.
            </p>
          </div>
        </section>

        {/* Responsibilities Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Responsibilities</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.responsibilitiesGrid}>
            {responsibilities.map((responsibility, index) => (
              <div 
                key={index}
                className={styles.responsibilityCard}
                style={{ animationDelay: `${index * 0.1}s` }}
                onMouseEnter={() => setHoveredResponsibility(index)}
                onMouseLeave={() => setHoveredResponsibility(null)}
              >
                <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                <div className={styles.cardIcon}>{responsibility.icon}</div>
                <div className={styles.cardText}>{responsibility.text}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Tools Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Tools Used</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.toolsGrid}>
            {tools.map((tool, index) => (
              <div 
                key={index}
                className={styles.toolCard}
                style={{ animationDelay: `${index * 0.15}s` }}
                onMouseEnter={() => setHoveredTool(index)}
                onMouseLeave={() => setHoveredTool(null)}
              >
                <div className={styles.toolGlow}></div>
                <div className={styles.toolIcon}>{tool.icon}</div>
                <div className={styles.toolName}>{tool.name}</div>
                <div className={styles.toolDescription}>{tool.description}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Skills Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.skillsContainer}>
            {skills.map((skill, index) => (
              <div 
                key={index}
                className={styles.skillTag}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <span className={styles.skillHash}>#</span>
                {skill}
              </div>
            ))}
          </div>
        </section>

        
      </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer />
      </div>
    </>
  );
};

export default AwfismojoProjectShowcase;